import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface SoinsSectionProps {
  suiteId: string;
  sessions: any[];
  medecinId: string;
  suiteTerminee: boolean;
  onRefresh: () => void;
}

const inputClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors text-sm bg-white';

interface SoinLigne {
  id: string;
  description: string;
}

export default function SoinsSection({
  suiteId,
  sessions,
  medecinId,
  suiteTerminee,
  onRefresh,
}: SoinsSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Session en cours d'édition (id + description)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDesc, setEditingDesc] = useState('');
  const [editingSaving, setEditingSaving] = useState(false);
  const [soins, setSoins] = useState<SoinLigne[]>([{ id: '1', description: '' }]);

  const addSoin = () =>
    setSoins((prev) => [...prev, { id: Date.now().toString(), description: '' }]);
  const updateSoin = (id: string, v: string) =>
    setSoins((prev) => prev.map((s) => (s.id === id ? { ...s, description: v } : s)));
  const removeSoin = (id: string) => {
    if (soins.length <= 1) return;
    setSoins((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSave = async () => {
    const valides = soins.filter((s) => s.description.trim());
    if (valides.length === 0) {
      setErrorMsg('Décrivez au moins un soin à administrer.');
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase.from('sessions_soins').insert(
        valides.map((s) => ({
          suite_soin_id: suiteId,
          plainte_id: null,
          description: s.description.trim(),
          date_soin: new Date().toISOString().split('T')[0],
          medecin_id: medecinId || null,
        }))
      );
      if (error) throw error;
      setSoins([{ id: Date.now().toString(), description: '' }]);
      setShowForm(false);
      onRefresh();
    } catch (err: any) {
      setErrorMsg(err.message || "Erreur lors de l'enregistrement des soins.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s: any) => {
    setEditingId(s.id);
    setEditingDesc(s.description || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDesc('');
  };

  const saveEdit = async (id: string) => {
    if (!editingDesc.trim()) {
      setErrorMsg('La description du soin ne peut pas être vide.');
      return;
    }
    setEditingSaving(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase
        .from('sessions_soins')
        .update({ description: editingDesc.trim() })
        .eq('id', id);
      if (error) throw error;
      setEditingId(null);
      setEditingDesc('');
      onRefresh();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de la modification du soin.');
    } finally {
      setEditingSaving(false);
    }
  };

  const deleteSession = async (id: string) => {
    if (!window.confirm('Supprimer ce soin de la fiche de soin ?')) return;
    setErrorMsg(null);
    try {
      const { error } = await supabase.from('sessions_soins').delete().eq('id', id);
      if (error) throw error;
      onRefresh();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de la suppression du soin.');
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Fiche de Soin
        </h2>
        <div className="flex items-center gap-2">
          {!suiteTerminee && (
            <button
              onClick={() => { setShowForm(!showForm); setErrorMsg(null); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                showForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
              }`}
            >
              <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
              {showForm ? 'Retirer' : 'Ajouter un soin'}
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2 mb-4">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {showForm && (
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mb-4 space-y-1">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                Date du soin : {new Date().toLocaleDateString('fr-FR')} (définie automatiquement)
              </p>
            </div>
            <div className="space-y-2">
              {soins.map((s) => (
                <div key={s.id} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={s.description}
                      onChange={(e) => updateSoin(s.id, e.target.value)}
                      className={inputClass}
                      placeholder="Soin à administrer..."
                    />
                  </div>
                  {soins.length > 1 && (
                    <button
                      onClick={() => removeSoin(s.id)}
                      type="button"
                      className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 mt-1"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addSoin}
              type="button"
              className="px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-add-line"></i>Ajouter un soin
            </button>
            <div className="pt-2 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer les soins
              </button>
            </div>
          </div>
        )}

        {sessions.length > 0 ? (
          <div className={showForm ? 'mt-6 space-y-2' : 'space-y-2'}>
            {sessions.map((s) => (
              <div key={s.id} className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <div className="bg-blue-100 rounded-lg px-2 py-1 text-[11px] font-semibold text-blue-700 whitespace-nowrap flex-shrink-0">
                  {new Date(s.date_soin).toLocaleDateString('fr-FR')}
                </div>
                {editingId === s.id ? (
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={editingDesc}
                      onChange={(e) => setEditingDesc(e.target.value)}
                      className={inputClass}
                      autoFocus
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => saveEdit(s.id)}
                        disabled={editingSaving}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                      >
                        {editingSaving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                        Enregistrer
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-300 cursor-pointer whitespace-nowrap"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-blue-900 flex-1 whitespace-pre-wrap">{s.description}</p>
                    {!suiteTerminee && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => startEdit(s)}
                          title="Modifier ce soin"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-blue-500 hover:bg-blue-100 cursor-pointer transition-colors"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => deleteSession(s.id)}
                          title="Supprimer ce soin"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors"
                        >
                          <i className="ri-delete-bin-6-line"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        ) : !showForm ? (
          <div className="text-center py-8">
            <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <i className="ri-nurse-line text-3xl text-gray-300"></i>
              <p className="text-sm text-gray-500 mt-2">Ajoutez une fiche de soin si des soins infirmiers sont nécessaires.</p>
              {!suiteTerminee && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-3 px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-add-line mr-1"></i>Ajouter une fiche de soin
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}